// Copyright © 2021 Move Closer
import { __decorate } from "tslib";
import VueScroll from 'vuescroll';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { BootstrapBadge, BootstrapLink } from '../../../dsl/front';
import { LabelColor } from '../../_contracts';
import { ReadMoreLink } from '../ReadMoreLink';
import { VUESCROLL_OPTIONS } from './TextList.config';
Vue.use(VueScroll, VUESCROLL_OPTIONS);
/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 */
let TextList = class TextList extends Vue {
    get showBadge() {
        if (!this.listConfig) {
            return false;
        }
        if (typeof this.listConfig.showArticleTags === 'undefined') {
            return false;
        }
        return this.listConfig.showArticleTags;
    }
    /**
     * Searches the article's badges (labels) for the 1st one in the red color
     * and returns it if found.
     *
     * @param article - The article to search within.
     */
    getRedBadge(article) {
        if (typeof article.properties.labels !== 'object' &&
            article.properties.labels !== null &&
            Array.isArray(article.properties.labels) &&
            article.properties.labels.length > 0) {
            return article.properties.labels.find(label => label.color === LabelColor.Red);
        }
        return undefined;
    }
    /**
     * Resolves the CSS modifier class that describes the site
     * that the given article belongs to.
     *
     * @param article - The source article.
     */
    getSiteModifier(article) {
        if (typeof article === 'undefined' || typeof article.site !== 'number') {
            return '';
        }
        return `--site-${article.site}`;
    }
    /**
     * Method to get proper article title
     *
     * @param article - The article to render.
     */
    getTitle(article) {
        if (!this.listConfig) {
            return article.title;
        }
        if (!this.listConfig.titleVersion) {
            return article.title;
        }
        if (typeof article.properties.additionalTitles !== 'object' || article.properties.additionalTitles === null ||
            !article.properties.additionalTitles[this.listConfig.titleVersion]) {
            return article.title;
        }
        return article.properties.additionalTitles[this.listConfig.titleVersion];
    }
    getPublicationDateParsed(article) {
        const date = new Date(article.publicationDate);
        if (isNaN(date.getTime())) {
            return '';
        }
        const now = new Date();
        const isToday = date.getDate() === now.getDate() &&
            date.getMonth() === now.getMonth() &&
            date.getFullYear() === now.getFullYear();
        if (isToday) {
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${hours}:${minutes}`;
        }
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    }
    /**
     * Determines whether the passed-in article has any red badges (labels) associated.
     *
     * @param article - The article to check.
     */
    hasRedBadge(article) {
        return typeof this.getRedBadge(article) !== 'undefined';
    }
};
__decorate([
    Prop({ type: Array, required: true })
], TextList.prototype, "articles", void 0);
__decorate([
    Prop({ type: Object, required: false })
], TextList.prototype, "readMoreLink", void 0);
__decorate([
    Prop({ type: Boolean, default: false })
], TextList.prototype, "withoutBadge", void 0);
__decorate([
    Prop({ type: Boolean, default: false })
], TextList.prototype, "showPublicationDate", void 0);
__decorate([
    Prop({ type: Object, required: false, default: () => { return { showArticleTags: false }; } })
], TextList.prototype, "listConfig", void 0);
TextList = __decorate([
    Component({
        name: 'TextList',
        components: { BootstrapBadge, BootstrapLink, ReadMoreLink, VueScroll },
        template: `
    <div v-if="articles && articles.length" class="text-list">
      <div class="text-list__container" :class="{ '--with-read-more' : readMoreLink }">
        <VueScroll>
          <!-- Articles list -->
          <ul class="text-list__list list-unstyled">
            <li v-for="article in articles" :key="article.id"
                class="text-list__list__item" :class="getSiteModifier(article)">
              <BootstrapLink :link="{ target: { path: article.urlPath } }"
                             v-clamp="3" class="stretched-link">
                <BootstrapBadge v-if="hasRedBadge(article) && showBadge && !withoutBadge" class="text-list__list__item__badge">
                  {{ getRedBadge(article).name }}
                </BootstrapBadge>
                <div class="text-list__list__item__title">
                  <time v-if="showPublicationDate" :datetime="article.publicationDate" class="text-list__list__item__publication-date">
                    {{ getPublicationDateParsed(article) }} |
                  </time>
                  {{ getTitle(article) }}
                </div>
              </BootstrapLink>
            </li>
          </ul>
          <!-- END | Articles list -->
        </VueScroll>
      </div>

      <!-- "Read more" button -->
      <ReadMoreLink v-if="readMoreLink" :link="readMoreLink"
                    class="text-list__read-more-link" />

    </div>
  `
    })
], TextList);
export { TextList };
